/**
 * PipelineGatePage — /omega/pipeline/gate
 * Sprint 38 Phase 41.B: Test Brain — validate a ProductSpec against discovered
 * pain, generate a test plan, and approve at Founder Gate 1 (spec approval).
 */
import { useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { apiFetch } from '@/api/http';

// ─── API response shapes (C5 contract) ─────────────────────────────

interface ProductSpecDraft {
  id: string;
  title: string;
  target_vertical: string;
  pain_summary: string;
  solution_sketch: string;
  created_at_unix: number;
  pricing_context: null;
}

type Confidence = 'low' | 'medium' | 'high';

interface SpecAlignmentScore {
  spec_id: string;
  score: number;
  addressed: string[];
  gaps: string[];
  confidence: Confidence;
  validated_at_unix: number;
}

interface TestCase {
  id: string;
  name: string;
  kind: string;
  expected_output: string;
  priority: string;
}

interface TestPlan {
  spec_id: string;
  cases: TestCase[];
  estimated_coverage_pct: number;
  critical_paths: string[];
  generated_at_unix: number;
}

interface CoverageReport {
  spec_id: string;
  tier: string;
  required_pct: number;
  actual_pct: number;
  pass: boolean;
  audited_at_unix: number;
}

interface GenerateTestsResponse {
  plan: TestPlan;
  coverage: CoverageReport;
}

type GateDecision = 'approved' | 'rejected';

interface GateRequest {
  decision: GateDecision;
  founder_sig: string;
  note?: string;
}

interface GateResponse {
  status: string;
  gate: string;
  decision: GateDecision;
}

// ─── Small presentational helpers ──────────────────────────────────

// Full 12-vertical GI taxonomy — canonical kebab-case GICategory names
// (must match the backend learning loop, which skips unrecognised names).
const VERTICALS = [
  'commerce-and-retail',
  'work-and-productivity',
  'industry-and-manufacturing',
  'financial',
  'health-and-wellbeing',
  'technology-and-infrastructure',
  'knowledge-and-education',
  'media-and-entertainment',
  'real-estate-and-property',
  'government-and-civic',
  'mobility-and-transport',
  'personal-and-lifestyle',
];

function scoreColor(score: number): string {
  if (score >= 70) return '#16a34a';
  if (score >= 40) return '#d97706';
  return '#dc2626';
}

function ConfidencePill({ confidence }: { confidence: Confidence }) {
  const colors: Record<Confidence, string> = {
    high: '#16a34a',
    medium: '#d97706',
    low: '#dc2626',
  };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 4,
      background: colors[confidence], color: '#fff', fontSize: 12, fontWeight: 600,
    }}>
      {confidence} confidence
    </span>
  );
}

function PassPill({ pass }: { pass: boolean }) {
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 4,
      background: pass ? '#16a34a' : '#dc2626', color: '#fff', fontSize: 12, fontWeight: 600,
    }}>
      {pass ? 'PASS' : 'FAIL'}
    </span>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '6px 10px', borderRadius: 4,
  border: '1px solid #d1d5db', fontSize: 13,
};
const labelStyle: React.CSSProperties = {
  fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4,
};
const panelStyle: React.CSSProperties = {
  background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8,
  padding: 16, marginBottom: 24,
};

export function PipelineGatePage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  // Validate-spec form state.
  const [title, setTitle] = useState('');
  const [targetVertical, setTargetVertical] = useState<string>('commerce-and-retail');
  const [painSummary, setPainSummary] = useState('');
  const [solutionSketch, setSolutionSketch] = useState('');

  // Persisted across mutations — the spec we validated and its id.
  const [draft, setDraft] = useState<ProductSpecDraft | null>(null);
  const [score, setScore] = useState<SpecAlignmentScore | null>(null);

  // Gate 1 controls.
  const [founderSig, setFounderSig] = useState('');
  const [note, setNote] = useState('');

  const validateMutation = useMutation<SpecAlignmentScore, Error, ProductSpecDraft>({
    mutationFn: (spec) =>
      apiFetch<SpecAlignmentScore>('/admin/pipeline/validate', { method: 'POST', body: spec }),
    onSuccess: (result, spec) => {
      setDraft(spec);
      setScore(result);
    },
  });

  const generateMutation = useMutation<GenerateTestsResponse, Error, void>({
    mutationFn: () => {
      if (!draft) throw new Error('No validated spec.');
      return apiFetch<GenerateTestsResponse>(
        `/admin/pipeline/generate-tests/${draft.id}`,
        { method: 'POST', body: draft },
      );
    },
  });

  const gateMutation = useMutation<GateResponse, Error, GateRequest>({
    mutationFn: (req) => {
      if (!draft) throw new Error('No validated spec.');
      return apiFetch<GateResponse>(
        `/admin/pipeline/gate/${draft.id}/spec`,
        { method: 'POST', body: req },
      );
    },
  });

  const handleValidate = () => {
    // These run in the browser at click time (event handler), so the
    // standard global crypto + Date APIs are the correct source of a
    // fresh UUID and the current unix-seconds timestamp.
    const spec: ProductSpecDraft = {
      id: crypto.randomUUID(),
      title,
      target_vertical: targetVertical,
      pain_summary: painSummary,
      solution_sketch: solutionSketch,
      created_at_unix: Math.floor(Date.now() / 1000),
      pricing_context: null,
    };
    validateMutation.mutate(spec);
  };

  const handleGate = (decision: GateDecision) => {
    const req: GateRequest = { decision, founder_sig: founderSig };
    if (note) req.note = note;
    gateMutation.mutate(req);
  };

  const gen = generateMutation.data;

  return (
    <div style={{ padding: 24, maxWidth: 1100 }}>
      <h1 style={{ marginBottom: 4 }}>Pipeline Gate</h1>
      <p style={{ color: '#6b7280', marginBottom: 20 }}>
        Phase 41.B — Test Brain: validate a ProductSpec and approve at Gate 1.{' '}
        <Link to="/omega/research/teams">← Research Teams</Link>
      </p>

      {/* ── 1. Validate-Spec form ── */}
      <div style={panelStyle}>
        <h3 style={{ margin: '0 0 12px' }}>Validate ProductSpec</h3>
        <div style={{ display: 'grid', gap: 12 }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: 2, minWidth: 220 }}>
              <label style={labelStyle}>Title</label>
              <input type="text" value={title} onChange={e => setTitle(e.target.value)}
                placeholder="e.g. Automated invoice reconciliation" style={inputStyle} />
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <label style={labelStyle}>Target vertical</label>
              <select value={targetVertical} onChange={e => setTargetVertical(e.target.value)}
                style={inputStyle}>
                {VERTICALS.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label style={labelStyle}>Pain summary</label>
            <textarea value={painSummary} onChange={e => setPainSummary(e.target.value)}
              rows={3} placeholder="The discovered pain this spec claims to address."
              style={{ ...inputStyle, resize: 'vertical' }} />
          </div>
          <div>
            <label style={labelStyle}>Solution sketch</label>
            <textarea value={solutionSketch} onChange={e => setSolutionSketch(e.target.value)}
              rows={3} placeholder="How the product solves the pain."
              style={{ ...inputStyle, resize: 'vertical' }} />
          </div>
          <div>
            <button onClick={handleValidate}
              disabled={validateMutation.isPending || !title || !painSummary || !solutionSketch}
              style={{ padding: '8px 16px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
              {validateMutation.isPending ? 'Validating…' : 'Validate Spec'}
            </button>
          </div>
        </div>
        {validateMutation.isError && (
          <p style={{ color: '#dc2626', marginTop: 8, fontSize: 12 }}>
            Validation failed: {validateMutation.error.message}
          </p>
        )}
      </div>

      {/* ── 2. SpecAlignmentScore panel ── */}
      {score && (
        <div style={panelStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 24, marginBottom: 12 }}>
            <div style={{ fontSize: 56, fontWeight: 700, lineHeight: 1, color: scoreColor(score.score) }}>
              {score.score}
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Alignment score (0–100)</div>
              <ConfidencePill confidence={score.confidence} />
            </div>
          </div>
          {score.score < 40 && (
            <p style={{ color: '#dc2626', fontWeight: 600, fontSize: 13, marginTop: 0 }}>
              Below abort threshold (40) — pipeline would abort.
            </p>
          )}
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 240 }}>
              <h4 style={{ margin: '0 0 6px', fontSize: 13 }}>Addressed</h4>
              {score.addressed.length === 0 ? (
                <p style={{ color: '#6b7280', fontSize: 13, margin: 0 }}>—</p>
              ) : (
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
                  {score.addressed.map((a, i) => <li key={i} style={{ color: '#16a34a' }}>{a}</li>)}
                </ul>
              )}
            </div>
            <div style={{ flex: 1, minWidth: 240 }}>
              <h4 style={{ margin: '0 0 6px', fontSize: 13 }}>Gaps</h4>
              {score.gaps.length === 0 ? (
                <p style={{ color: '#6b7280', fontSize: 13, margin: 0 }}>—</p>
              ) : (
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
                  {score.gaps.map((g, i) => <li key={i} style={{ color: '#dc2626' }}>{g}</li>)}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── 3. Generate Test Plan ── */}
      {score && (
        <div style={panelStyle}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <h3 style={{ margin: 0 }}>Test Plan</h3>
            <button onClick={() => generateMutation.mutate()}
              disabled={generateMutation.isPending}
              style={{ padding: '8px 16px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
              {generateMutation.isPending ? 'Generating…' : 'Generate Test Plan'}
            </button>
          </div>
          {generateMutation.isError && (
            <p style={{ color: '#dc2626', fontSize: 12 }}>
              Generation failed: {generateMutation.error.message}
            </p>
          )}
          {gen && (
            <>
              <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13, marginBottom: 16 }}>
                <thead>
                  <tr style={{ background: '#f3f4f6' }}>
                    {['Name', 'Kind', 'Priority', 'Expected output'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '8px 12px', border: '1px solid #e5e7eb', fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {gen.plan.cases.map(c => (
                    <tr key={c.id}>
                      <td style={{ padding: '8px 12px', border: '1px solid #e5e7eb' }}>{c.name}</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e5e7eb', color: '#6b7280' }}>{c.kind}</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e5e7eb' }}>{c.priority}</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e5e7eb', color: '#6b7280' }}>{c.expected_output}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {gen.plan.critical_paths.length > 0 && (
                <p style={{ fontSize: 13, margin: '0 0 16px' }}>
                  <strong>Critical paths:</strong> {gen.plan.critical_paths.join(', ')}
                </p>
              )}

              {/* CoverageReport summary */}
              <div style={{ display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap', fontSize: 13 }}>
                <div><strong>Coverage</strong></div>
                <div>Tier: {gen.coverage.tier}</div>
                <div>Required: {gen.coverage.required_pct}%</div>
                <div>Actual: {gen.coverage.actual_pct}%</div>
                <PassPill pass={gen.coverage.pass} />
              </div>
            </>
          )}
        </div>
      )}

      {/* ── 4. Gate 1 controls ── */}
      {score && (
        <div style={panelStyle}>
          <h3 style={{ margin: '0 0 12px' }}>Gate 1 — Spec Approval</h3>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <label style={labelStyle}>Founder signature</label>
              <input type="text" value={founderSig} onChange={e => setFounderSig(e.target.value)}
                placeholder="founder_sig" style={inputStyle} />
            </div>
            <div style={{ flex: 2, minWidth: 220 }}>
              <label style={labelStyle}>Note (optional)</label>
              <input type="text" value={note} onChange={e => setNote(e.target.value)}
                placeholder="Reason / context" style={inputStyle} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <button onClick={() => handleGate('approved')}
              disabled={gateMutation.isPending || !founderSig}
              style={{ padding: '8px 16px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
              {gateMutation.isPending ? 'Submitting…' : 'Approve Spec'}
            </button>
            <button onClick={() => handleGate('rejected')}
              disabled={gateMutation.isPending || !founderSig}
              style={{ padding: '8px 16px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
              Reject
            </button>
          </div>
          {gateMutation.isError && (
            <p style={{ color: '#dc2626', marginTop: 8, fontSize: 12 }}>
              Gate submission failed: {gateMutation.error.message}
            </p>
          )}
          {gateMutation.isSuccess && gateMutation.data && (
            <p style={{ color: '#16a34a', marginTop: 8, fontSize: 13 }}>
              Gate {gateMutation.data.gate} {gateMutation.data.decision} (status: {gateMutation.data.status}).
            </p>
          )}
        </div>
      )}
    </div>
  );
}
